/**
 * SOLOMON — LEAD SEARCH PANEL
 * Phase 1.5 §4.4 — operator-facing surface for BatchData lead generation.
 * 2026-05-20 geo-flex rewrite: no hardcoded ZIPs default. Accepts any
 * geographic identifier BatchData supports (zip / city+state / county+state /
 * state) via a freeform textarea + chip-loadable saved markets.
 *
 * Spec: docs/superpowers/specs/2026-05-20-geo-flex-search-design.md
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSolomon } from '../../context/SolomonContext';
import { ActionCard } from '../ActionCard';
import GeoTextarea, { serializeIdentifiers } from './GeoTextarea.jsx';
import SavedMarketChip from './SavedMarketChip.jsx';
import CostSubtitle from '../CostSubtitle.jsx';
import ConfirmCostDialog from '../ConfirmCostDialog.jsx';
import { useCostMap } from '../../hooks/useCostMap.js';
import './leadSearchPanel.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Tier 1 preset chip groups — names map to BatchData quickLists keys.
const DISTRESS_OPTIONS = [
  { key: 'preforeclosure',   label: 'Pre-foreclosure' },
  { key: 'taxDefault',       label: 'Tax delinquent' },
  { key: 'involuntaryLien',  label: 'Tax / involuntary lien' },
  { key: 'noticeOfDefault',  label: 'Notice of default' },
  { key: 'noticeOfSale',     label: 'Notice of sale' },
  { key: 'activeAuction',    label: 'Auction scheduled' },
];

const MOTIVATION_OPTIONS = [
  { key: 'absenteeOwner',         label: 'Absentee owner' },
  { key: 'outOfStateOwner',       label: 'Out-of-state owner' },
  { key: 'vacant',                label: 'Vacant' },
  { key: 'mailingAddressVacant',  label: 'Mailing addr vacant' },
  { key: 'seniorOwner',           label: 'Senior owner' },
  { key: 'inherited',             label: 'Inherited' },
  { key: 'tiredLandlord',         label: 'Tired landlord' },
  { key: 'expiredListing',        label: 'Expired listing' },
  { key: 'failedListing',         label: 'Failed listing' },
];

const DEAL_OPTIONS = [
  { key: 'highEquity',  label: 'High equity' },
  { key: 'freeAndClear', label: 'Free & clear' },
];

const PER_RECORD_CENTS  = 8;
// One search fans out over the server's 9 AND-stacks (SERVER_QUICKLIST_STACKS
// in leadGenerationService.js) — each stack is its own billed call. Preview
// only; the authoritative estimate comes from the server dry-run at submit.
const STACK_COUNT = 9;

const DEFAULT_FORM = {
  geoText:        '',                              // empty default — operator picks a saved market or types
  value_min:       30000,
  value_max:       300000,
  equity_pct_min:  30,
  take:            50,
  exclude_corporate: true,
  distress_signals:   ['preforeclosure', 'taxDefault', 'involuntaryLien'],
  motivation_signals: ['absenteeOwner', 'vacant', 'seniorOwner', 'inherited'],
  deal_viability:     ['highEquity', 'freeAndClear'],
};

export default function LeadSearchPanel() {
  const { authFetch } = useAuth();
  const {
    pendingActions,
    addPendingAction,
    approveAction: approveActionCtx,
    cancelAction:  cancelActionCtx,
  } = useSolomon();

  const [form, setForm]                   = useState(DEFAULT_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [searchError, setSearchError]     = useState(null);
  const [searchResult, setSearchResult]   = useState(null);
  const [sourceListName, setSourceListName] = useState('');
  const [proposing, setProposing]         = useState(false);
  const [proposeError, setProposeError]   = useState(null);
  const [proposeStatus, setProposeStatus] = useState(null);
  const [localActionId, setLocalActionId] = useState(null);
  const [parseErrors, setParseErrors]     = useState([]);
  const { costMap, threshold } = useCostMap();
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);
  // Server dry-run estimate for the pending pull — the amount the operator
  // confirms and the confirmCents the real run sends back (cost gate).
  const [pullEstimate, setPullEstimate] = useState(null);

  // ── Saved markets state ─────────────────────────────────────
  const [savedMarkets, setSavedMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [marketsError, setMarketsError] = useState(null);
  const [editingMarket, setEditingMarket] = useState(null);    // {id?, name, identifiersText} or null
  const [marketSaveError, setMarketSaveError] = useState(null);

  // Load saved markets on mount — backend auto-seeds defaults on first GET.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMarketsLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/api/saved-markets`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success) setSavedMarkets(data.data || []);
        else setMarketsError(data.error || 'Failed to load saved markets');
      } catch (err) {
        if (!cancelled) setMarketsError(err.message);
      } finally {
        if (!cancelled) setMarketsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // ── Derived ─────────────────────────────────────────────────
  const liveLineCount = useMemo(() => {
    if (!form.geoText) return 0;
    return form.geoText.split(/\r?\n/).filter((l) => l.trim()).length;
  }, [form.geoText]);

  // Cost projection — line-count proxy for identifier count. Exact count
  // confirmed server-side at submit (dry-run estimate). Live preview only.
  const projectedMaxCostCents   = liveLineCount * STACK_COUNT * Math.max(1, Number(form.take) || 0) * PER_RECORD_CENTS;
  const projectedMaxCostUsd     = (projectedMaxCostCents / 100).toFixed(2);

  const toggleSignal = (bucket, key) => {
    setForm(f => {
      const set = new Set(f[bucket]);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...f, [bucket]: Array.from(set) };
    });
  };

  // ── Saved markets actions ───────────────────────────────────
  const handleLoadMarket = (market) => {
    const text = serializeIdentifiers(market.identifiers);
    setForm((f) => ({
      ...f,
      geoText: f.geoText.trim() ? `${f.geoText.trim()}\n${text}` : text,
    }));
    setParseErrors([]);
  };

  const handleClearGeo = () => {
    setForm((f) => ({ ...f, geoText: '' }));
    setParseErrors([]);
  };

  const handleEditMarket = (market) => {
    setEditingMarket({
      id: market.id,
      name: market.name,
      identifiersText: serializeIdentifiers(market.identifiers),
    });
    setMarketSaveError(null);
  };

  const handleNewMarket = () => {
    setEditingMarket({ name: '', identifiersText: '' });
    setMarketSaveError(null);
  };

  const handleDeleteMarket = async (market) => {
    if (!confirm(`Delete "${market.name}"?`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/saved-markets/${market.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setSavedMarkets((arr) => arr.filter((m) => m.id !== market.id));
    } catch (err) {
      setMarketsError(err.message);
    }
  };

  const handleSaveMarketForm = async () => {
    if (!editingMarket) return;
    setMarketSaveError(null);
    const { id, name, identifiersText } = editingMarket;
    if (!name.trim()) { setMarketSaveError('Name required'); return; }
    if (!identifiersText.trim()) { setMarketSaveError('At least one identifier required'); return; }
    try {
      const url = id ? `${API_BASE}/api/saved-markets/${id}` : `${API_BASE}/api/saved-markets`;
      const method = id ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), identifiers_text: identifiersText }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        // Server may include parse errors for malformed identifier text
        if (data?.data?.parse_errors?.length) {
          const first = data.data.parse_errors[0];
          throw new Error(`Line ${first.line}: ${first.message}`);
        }
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const saved = data.data;
      setSavedMarkets((arr) => {
        const idx = arr.findIndex((m) => m.id === saved.id);
        if (idx >= 0) {
          const copy = arr.slice();
          copy[idx] = saved;
          return copy;
        }
        return [...arr, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setEditingMarket(null);
    } catch (err) {
      setMarketSaveError(err.message);
    }
  };

  // ── Submit search (server-gated two-step, 2026-07-01 incident fix) ──
  // Step 1 (dry-run, always $0): server returns the worst-case estimate for
  // THIS run. Step 2 (real): the request carries confirmCents — the server
  // refuses (402) any paid pull whose estimate wasn't confirmed, so a stale
  // client can no longer trigger unconfirmed spend.
  const buildSearchBody = () => ({
    identifiers_text: form.geoText,
    value_min:      Number(form.value_min) || undefined,
    value_max:      Number(form.value_max) || undefined,
    equity_pct_min: Number(form.equity_pct_min) || undefined,
    take:           Number(form.take) || undefined,
    preset: {
      distress_signals:   form.distress_signals,
      motivation_signals: form.motivation_signals,
      deal_viability:     form.deal_viability,
      exclude_corporate:  !!form.exclude_corporate,
    },
  });

  // Step 1: free dry-run → estimate → confirm dialog (or straight to run
  // when the capped estimate is under the confirm threshold).
  const handleSearchClick = async (e) => {
    e?.preventDefault?.();
    setSearchError(null);
    setParseErrors([]);
    setPullEstimate(null);

    if (!form.geoText.trim()) {
      setSearchError('Enter at least one geographic identifier');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/search`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...buildSearchBody(), dryRun: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data?.data?.parse_errors?.length) setParseErrors(data.data.parse_errors);
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const est = data.data;
      setPullEstimate(est);
      const thresholdCents = Math.round((threshold ?? 2) * 100);
      if ((est.cappedEstCents ?? 0) > thresholdCents) {
        setSearchDialogOpen(true);          // operator confirms the dollar amount
      } else {
        await handleSearch(est.cappedEstCents);
      }
    } catch (err) {
      setSearchError(err.message || 'Search failed');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  // Step 2: the confirmed paid run.
  const handleSearch = async (confirmCents) => {
    setSearchError(null);
    setSearchResult(null);
    setProposeError(null);
    setProposeStatus(null);
    setLocalActionId(null);
    setParseErrors([]);
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/search`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...buildSearchBody(), confirmCents }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data?.data?.parse_errors?.length) {
          setParseErrors(data.data.parse_errors);
        }
        // Server refused: estimate changed since the dry-run (form edited,
        // env cap changed). Re-open the confirm with the fresh numbers.
        if (res.status === 402 && data?.data?.requiresConfirmation) {
          setPullEstimate(data.data);
          setSearchDialogOpen(true);
          return;
        }
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setSearchResult(data.data);
    } catch (err) {
      setSearchError(err.message || 'Search failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Propose ingest ──────────────────────────────────────────
  const handleProposeIngest = async () => {
    if (!searchResult?.queryId) return;
    setProposeError(null);
    setProposeStatus(null);
    setProposing(true);
    try {
      const res = await authFetch(
        `${API_BASE}/api/leads/search/${searchResult.queryId}/propose-ingest`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceListName: sourceListName.trim() || null }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const action = data.data?.action;
      if (action?.status === 'pending') {
        addPendingAction(action);
        setLocalActionId(action.id);
        setProposeStatus('Proposed — approve below to write leads.');
      } else if (action?.status === 'rejected' || action?.status === 'queued') {
        setProposeError(action.reason || `Action ${action.status}`);
      } else {
        setProposeStatus('Proposed for approval.');
      }
    } catch (err) {
      setProposeError(err.message || 'Failed to propose');
    } finally {
      setProposing(false);
    }
  };

  const handleApproveLocal = async (actionId) => {
    try {
      await approveActionCtx(actionId);
      setLocalActionId(null);
      setProposeStatus('Approved — leads ingested.');
      setTimeout(() => setSearchResult(null), 1500);
    } catch (err) {
      setProposeError(err.message || 'Approval failed');
    }
  };

  const handleCancelLocal = async (actionId) => {
    try {
      await cancelActionCtx(actionId);
      setLocalActionId(null);
      setProposeStatus('Cancelled.');
    } catch (err) {
      setProposeError(err.message || 'Cancel failed');
    }
  };

  const localAction = localActionId ? pendingActions.find(a => a.id === localActionId) : null;

  // ── 0-result action handlers ────────────────────────────────
  const handleEditSearch = () => {
    // Scroll to and focus the textarea
    const ta = document.querySelector('.ls-geo-textarea');
    if (ta) ta.focus();
  };
  const handleAdjustFilters = () => {
    const fieldset = document.querySelector('.ls-fieldset');
    if (fieldset) fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="lead-search-panel">
      <form className="ls-form" onSubmit={handleSearchClick}>

        {/* Saved markets */}
        <div className="ls-row">
          <div className="ls-section-label">Saved markets</div>
          <div className="ls-markets-row">
            {marketsLoading && <span className="ls-hint">Loading…</span>}
            {!marketsLoading && savedMarkets.length === 0 && !marketsError && (
              <span className="ls-hint">No saved markets yet</span>
            )}
            {savedMarkets.map((m) => (
              <SavedMarketChip
                key={m.id}
                market={m}
                onLoad={handleLoadMarket}
                onEdit={handleEditMarket}
                onDelete={handleDeleteMarket}
              />
            ))}
            <button
              type="button"
              className="ls-market-new"
              onClick={handleNewMarket}
            >
              + new market
            </button>
          </div>
          {marketsError && <div className="ls-error">{marketsError}</div>}
        </div>

        {/* Market edit form */}
        {editingMarket && (
          <div className="ls-market-edit">
            <div className="ls-market-edit-title">
              {editingMarket.id ? 'Edit market' : 'New market'}
            </div>
            <input
              type="text"
              className="ls-input"
              placeholder="Market name (e.g. Indianapolis — Marion County)"
              value={editingMarket.name}
              onChange={(e) => setEditingMarket({ ...editingMarket, name: e.target.value })}
              autoFocus
            />
            <textarea
              className="ls-geo-textarea"
              rows={3}
              placeholder='Identifiers — one per line (e.g. "Marion County, IN" or "46322")'
              value={editingMarket.identifiersText}
              onChange={(e) => setEditingMarket({ ...editingMarket, identifiersText: e.target.value })}
            />
            <div className="ls-market-edit-actions">
              <button type="button" className="ls-market-save" onClick={handleSaveMarketForm}>
                Save
              </button>
              <button type="button" className="ls-market-cancel" onClick={() => setEditingMarket(null)}>
                Cancel
              </button>
            </div>
            {marketSaveError && <div className="ls-error">{marketSaveError}</div>}
          </div>
        )}

        {/* Geographic targets — textarea */}
        <div className="ls-row">
          <div className="ls-section-label">Geographic targets</div>
          <GeoTextarea
            value={form.geoText}
            onChange={(text) => setForm((f) => ({ ...f, geoText: text }))}
            onClear={handleClearGeo}
            parseErrors={parseErrors}
          />
        </div>

        {/* Value + equity */}
        <div className="ls-row ls-row-grid-3">
          <label className="ls-label">
            Value min ($)
            <input
              type="number"
              className="ls-input"
              value={form.value_min}
              onChange={e => setForm(f => ({ ...f, value_min: e.target.value }))}
              min={0}
            />
          </label>
          <label className="ls-label">
            Value max ($)
            <input
              type="number"
              className="ls-input"
              value={form.value_max}
              onChange={e => setForm(f => ({ ...f, value_max: e.target.value }))}
              min={0}
            />
          </label>
          <label className="ls-label">
            Equity % min
            <input
              type="number"
              className="ls-input"
              value={form.equity_pct_min}
              onChange={e => setForm(f => ({ ...f, equity_pct_min: e.target.value }))}
              min={0}
              max={100}
            />
          </label>
        </div>

        {/* Take + corporate */}
        <div className="ls-row ls-row-grid-2">
          <label className="ls-label">
            Records per identifier (take)
            <input
              type="number"
              className="ls-input"
              value={form.take}
              onChange={e => setForm(f => ({ ...f, take: e.target.value }))}
              min={1}
              max={1000}
            />
            <span className="ls-hint">Server caps at BATCHDATA_SEARCH_MAX_TAKE (default 250)</span>
          </label>
          <label className="ls-checkbox-label">
            <input
              type="checkbox"
              checked={form.exclude_corporate}
              onChange={e => setForm(f => ({ ...f, exclude_corporate: e.target.checked }))}
            />
            <span>Exclude LLC / trust / corporate ownership</span>
          </label>
        </div>

        {/* Signal chip groups */}
        <fieldset className="ls-fieldset">
          <legend>Distress signals</legend>
          <div className="ls-chips">
            {DISTRESS_OPTIONS.map(opt => (
              <ChipToggle key={opt.key} active={form.distress_signals.includes(opt.key)} onClick={() => toggleSignal('distress_signals', opt.key)} label={opt.label} />
            ))}
          </div>
        </fieldset>

        <fieldset className="ls-fieldset">
          <legend>Motivation signals</legend>
          <div className="ls-chips">
            {MOTIVATION_OPTIONS.map(opt => (
              <ChipToggle key={opt.key} active={form.motivation_signals.includes(opt.key)} onClick={() => toggleSignal('motivation_signals', opt.key)} label={opt.label} />
            ))}
          </div>
        </fieldset>

        <fieldset className="ls-fieldset">
          <legend>Deal viability</legend>
          <div className="ls-chips">
            {DEAL_OPTIONS.map(opt => (
              <ChipToggle key={opt.key} active={form.deal_viability.includes(opt.key)} onClick={() => toggleSignal('deal_viability', opt.key)} label={opt.label} />
            ))}
          </div>
        </fieldset>

        {/* Submit */}
        <div className="ls-submit-row">
          <div className="ls-cost-projection">
            Max billable: ~${projectedMaxCostUsd}
            <span className="ls-cost-detail">
              ({liveLineCount} area{liveLineCount === 1 ? '' : 's'} × {STACK_COUNT} stacks × {form.take || 0} records × $0.08/record — exact estimate confirmed before the run)
            </span>
          </div>
          <div className="button-with-cost">
            <button
              type="button"
              className="ls-submit-btn"
              disabled={submitting || liveLineCount === 0}
              onClick={handleSearchClick}
            >
              {submitting ? 'Searching…' : 'Search BatchData'}
            </button>
            <CostSubtitle buttonId="search" />
            <ConfirmCostDialog
              isOpen={searchDialogOpen}
              buttonId="search"
              leadId={null}
              estimate={pullEstimate ? {
                costLabel: `up to $${((pullEstimate.cappedEstCents ?? 0) / 100).toFixed(2)}`,
                note: `${pullEstimate.identifierCount} area(s) × ${pullEstimate.stacks} distress stacks × take ${pullEstimate.effectiveTake} × $0.08/record`
                  + (pullEstimate.estCentsMax > pullEstimate.cappedEstCents
                      ? ` — uncapped worst case $${(pullEstimate.estCentsMax / 100).toFixed(2)}, run stops at the $${(pullEstimate.maxRunCents / 100).toFixed(2)} ceiling`
                      : ''),
              } : null}
              onConfirm={() => {
                setSearchDialogOpen(false);
                handleSearch(pullEstimate?.cappedEstCents ?? 0);
              }}
              onCancel={() => setSearchDialogOpen(false)}
            />
          </div>
        </div>

        {searchError && <div className="ls-error">{searchError}</div>}
      </form>

      {/* Results */}
      {searchResult && (
        <div className="ls-results">
          {/* Partial-run banners (cost gate, 2026-07-01): the fan-out stopped
              early but everything already billed was kept and is ingestable. */}
          {searchResult.aborted && (
            <div className="ls-zero-warning ls-zero-warning--miss">
              <div className="ls-zero-warning-header">
                <span className="ls-zero-warning-icon">⚠</span>
                <strong>
                  Pull stopped mid-run ({searchResult.aborted.status === 403 ? 'BatchData balance exhausted' : searchResult.aborted.reason}) — showing the {searchResult.billableRecordCount} records already paid for (~${((searchResult.estSpentCents ?? 0) / 100).toFixed(2)}). They can still be ingested below.
                </strong>
              </div>
            </div>
          )}
          {searchResult.capStopped && !searchResult.aborted && (
            <div className="ls-zero-warning ls-zero-warning--filter">
              <div className="ls-zero-warning-header">
                <span className="ls-zero-warning-icon">⚠</span>
                <strong>
                  Pull stopped at the ${((searchResult.maxRunCents ?? 0) / 100).toFixed(2)} per-run spend ceiling — partial results below (~${((searchResult.estSpentCents ?? 0) / 100).toFixed(2)} spent). Narrow the area or lower take to fit a full pull under the cap.
                </strong>
              </div>
            </div>
          )}
          {/* Two-tier 0-result warnings — render FIRST so operator sees them prominently */}
          {Array.isArray(searchResult.zeroResultIdentifiers) && searchResult.zeroResultIdentifiers.length > 0 && (
            <div className="ls-zero-warning ls-zero-warning--miss">
              <div className="ls-zero-warning-header">
                <span className="ls-zero-warning-icon">⚠</span>
                <strong>
                  {searchResult.zeroResultIdentifiers.length} identifier{searchResult.zeroResultIdentifiers.length === 1 ? '' : 's'} returned 0 properties from BatchData
                </strong>
              </div>
              <div className="ls-zero-warning-body">
                {searchResult.zeroResultIdentifiers.map((g, i) => (
                  <div key={i} className="ls-zero-warning-item">
                    <code>{identifierToLabel(g)}</code> — possible causes: typo in the identifier, unsupported region, or a value/equity range set too tight (BatchData applies those server-side, so they look identical to a geographic miss).
                  </div>
                ))}
                <button type="button" className="ls-zero-action" onClick={handleEditSearch}>
                  Edit search
                </button>
              </div>
            </div>
          )}
          {Array.isArray(searchResult.allFilteredOutIdentifiers) && searchResult.allFilteredOutIdentifiers.length > 0 && (
            <div className="ls-zero-warning ls-zero-warning--filter">
              <div className="ls-zero-warning-header">
                <span className="ls-zero-warning-icon">⚠</span>
                <strong>
                  {searchResult.allFilteredOutIdentifiers.length} identifier{searchResult.allFilteredOutIdentifiers.length === 1 ? '' : 's'} returned raw matches but 0 qualified after preset filter
                </strong>
              </div>
              <div className="ls-zero-warning-body">
                {searchResult.allFilteredOutIdentifiers.map((entry, i) => (
                  <div key={i} className="ls-zero-warning-item">
                    <code>{identifierToLabel(entry.identifier)}</code> — {entry.raw_count} raw, 0 qualified.
                  </div>
                ))}
                <button type="button" className="ls-zero-action" onClick={handleAdjustFilters}>
                  Adjust filters
                </button>
              </div>
            </div>
          )}

          <div className="ls-results-summary">
            <div className="ls-stat"><span className="ls-stat-num">{searchResult.qualifiedCount}</span><span className="ls-stat-label">qualified</span></div>
            {searchResult.alreadyPulledCount > 0 && (
              <>
                <div className="ls-stat-divider">·</div>
                <div className="ls-stat ls-stat--dim">
                  <span className="ls-stat-num">{searchResult.alreadyPulledCount}</span>
                  <span className="ls-stat-label">already pulled</span>
                </div>
              </>
            )}
            <div className="ls-stat-divider">·</div>
            <div className="ls-stat ls-stat--accent">
              <span className="ls-stat-num">{searchResult.netNewQualifiedCount ?? searchResult.qualifiedCount}</span>
              <span className="ls-stat-label">net new</span>
            </div>
            <div className="ls-stat-divider">/</div>
            <div className="ls-stat"><span className="ls-stat-num">{searchResult.billableRecordCount}</span><span className="ls-stat-label">billable</span></div>
            <div className="ls-stat-divider">·</div>
            <div className="ls-stat"><span className="ls-stat-num">{
              searchResult.billableRecordCount > 0
                ? Math.round(searchResult.qualifiedCount / searchResult.billableRecordCount * 100) + '%'
                : '—'
            }</span><span className="ls-stat-label">funnel</span></div>
            <div className="ls-stat-divider">·</div>
            <div className="ls-stat"><span className="ls-stat-num">${(searchResult.billableRecordCount * PER_RECORD_CENTS / 100).toFixed(2)}</span><span className="ls-stat-label">spent</span></div>
          </div>

          <div className="ls-query-id">queryId: <code>{searchResult.queryId}</code> · cached 30 min</div>

          {Array.isArray(searchResult.signalCounts) && searchResult.signalCounts.length > 0 && (
            <div className="ls-signal-distribution">
              <div className="ls-signal-distribution-title">
                Signal distribution · {searchResult.qualifiedCount} qualified
              </div>
              <div className="ls-signal-distribution-chips">
                {searchResult.signalCounts.map(s => (
                  <span key={s.key} className={`ls-signal-count ls-signal-count--${s.severity || 'medium'}`}>
                    <span className="ls-signal-count-num">{s.count}</span>
                    <span className="ls-signal-count-label">{s.label}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {searchResult.sample && searchResult.sample.length > 0 && (
            <div className="ls-sample">
              <div className="ls-sample-title">First {searchResult.sample.length} qualified</div>
              {searchResult.sample.map((s, i) => (
                <div key={i} className="ls-sample-row">
                  <div className="ls-sample-addr">
                    {s.address?.street}, {s.address?.city} {s.address?.state} {s.address?.zip}
                  </div>
                  <div className="ls-sample-owner">{s.owner?.fullName || '—'}</div>
                  {Array.isArray(s.chips) && s.chips.length > 0 && (
                    <div className="ls-sample-chips">
                      {s.chips.map((chip, ci) => (
                        <span key={`${chip.key}-${ci}`} className={`ls-sample-chip ls-sample-chip--${chip.severity || 'medium'}`}>
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Propose ingest */}
          <div className="ls-ingest-row">
            <input
              type="text"
              className="ls-input"
              placeholder='Source list name (e.g. "Indianapolis_Marion_May2026")'
              value={sourceListName}
              onChange={e => setSourceListName(e.target.value)}
            />
            <div className="button-with-cost">
              <button
                type="button"
                className="ls-propose-btn"
                disabled={proposing || !!localActionId || (searchResult.netNewQualifiedCount ?? searchResult.qualifiedCount) === 0}
                onClick={() => {
                  const cost = costMap?.buttons?.propose_ingest?.cost_non_cached_usd_max ?? 0;
                  if (cost > (threshold ?? 2)) {
                    setIngestDialogOpen(true);
                  } else {
                    handleProposeIngest();
                  }
                }}
              >
                {proposing ? 'Proposing…' : 'Propose ingest'}
              </button>
              <CostSubtitle buttonId="propose_ingest" />
              <ConfirmCostDialog
                isOpen={ingestDialogOpen}
                buttonId="propose_ingest"
                leadId={null}
                onConfirm={() => { setIngestDialogOpen(false); handleProposeIngest(); }}
                onCancel={() => setIngestDialogOpen(false)}
              />
            </div>
          </div>
          {proposeError  && <div className="ls-error">{proposeError}</div>}
          {proposeStatus && <div className="ls-status">{proposeStatus}</div>}

          {localAction && (
            <div className="ls-action-slot">
              <ActionCard
                action={localAction}
                onApprove={handleApproveLocal}
                onCancel={handleCancelLocal}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function identifierToLabel(g) {
  if (!g) return '—';
  if (g.type === 'zip')    return `ZIP ${g.value}`;
  if (g.type === 'city')   return `${g.value}, ${g.state}`;
  if (g.type === 'county') return `${g.value}, ${g.state}`;
  if (g.type === 'state')  return g.value;
  return `${g.type}:${g.value}`;
}

function ChipToggle({ active, onClick, label }) {
  return (
    <button
      type="button"
      className={`ls-chip ${active ? 'ls-chip-active' : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
